import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useResponsive } from "../../../hooks/useResponsive";
import { setGroupUser } from "../../../redux/TaskGroupingSlice";

export function useTeamAccordionState({
  team,
  user,
  expandedPanel,
  handleExpandAccordianChange,
  setTeam,
  selectedUserIds = [],
  syncSelectedUsers,
  filteredItems,
  searchQuery,
}) {
  const dispatch = useDispatch();
  const isMobile = useResponsive("down", "md");

  const isExpandedFromPanel = useMemo(() => {
    return (
      expandedPanel.find((panel) => panel.team_id === team.team_id)?.isExpand ||
      false
    );
  }, [expandedPanel, team.team_id]);

  const [localExpanded, setLocalExpanded] = useState(false);
  const [manualOverride, setManualOverride] = useState(false);

  const shouldBeExpanded =
    searchQuery && !manualOverride ? isExpandedFromPanel : localExpanded;

  useEffect(() => {
    if (searchQuery && !manualOverride) {
      setLocalExpanded(isExpandedFromPanel);
    } else if (!searchQuery) {
      setLocalExpanded(false);
      setManualOverride(false);
    }
  }, [searchQuery, isExpandedFromPanel, manualOverride]);

  const teamUsers = useMemo(() => {
    if (!team.users.length || !filteredItems.length) return [];

    const selectedUserIdSet = new Set(
      user.map((selectedUser) => selectedUser.user_id)
    );

    return team.users
      .filter((teamUser) =>
        filteredItems.some((item) => item.user_id === teamUser.user_id)
      )
      .sort((a, b) => {
        const aSelected = selectedUserIdSet.has(a.user_id);
        const bSelected = selectedUserIdSet.has(b.user_id);

        if (aSelected !== bSelected) {
          return bSelected - aSelected;
        }

        return a.first_name.localeCompare(b.first_name);
      });
  }, [filteredItems, team.users, user]);

  const selectedUserIdSet = useMemo(() => {
    const ids = isMobile
      ? user.map((selectedUser) => String(selectedUser.user_id))
      : selectedUserIds;

    return new Set(ids.map(String));
  }, [isMobile, selectedUserIds, user]);

  const handleChange = useCallback(
    (event, isExpanded) => {
      setLocalExpanded(isExpanded);

      if (searchQuery) {
        setManualOverride(true);
      }

      handleExpandAccordianChange(team.team_id)(event, isExpanded);
    },
    [handleExpandAccordianChange, searchQuery, team.team_id]
  );

  const handleTeamCheckboxClick = useCallback(
    (event) => {
      event.stopPropagation();

      if (isMobile) {
        setTeam(
          team,
          team?.users?.every((teamUser) =>
            user.some((selectedUser) => selectedUser.user_id == teamUser.user_id)
          )
        );
        return;
      }

      const teamUserIds = teamUsers.map((teamUser) => String(teamUser.user_id));
      const shouldAdd = teamUserIds.some(
        (userId) => !selectedUserIdSet.has(userId)
      );
      const nextSelectedUserIds = shouldAdd
        ? [
            ...selectedUserIds,
            ...teamUserIds.filter((userId) => !selectedUserIdSet.has(userId)),
          ]
        : selectedUserIds.filter((userId) => !teamUserIds.includes(userId));

      syncSelectedUsers(nextSelectedUserIds, teamUsers);
    },
    [
      isMobile,
      selectedUserIdSet,
      selectedUserIds,
      setTeam,
      syncSelectedUsers,
      team,
      teamUsers,
      user,
    ]
  );

  const handleUserClick = useCallback(
    (clickedUser) => {
      if (isMobile) {
        dispatch(setGroupUser(clickedUser));
        return;
      }

      const clickedUserId = String(clickedUser.user_id);
      const nextSelectedUserIds = selectedUserIdSet.has(clickedUserId)
        ? selectedUserIds.filter((userId) => userId !== clickedUserId)
        : [...selectedUserIds, clickedUserId];

      syncSelectedUsers(nextSelectedUserIds, [clickedUser]);
    },
    [dispatch, isMobile, selectedUserIdSet, selectedUserIds, syncSelectedUsers]
  );

  return {
    handleChange,
    handleTeamCheckboxClick,
    handleUserClick,
    selectedUserIdSet,
    shouldBeExpanded,
    teamUsers,
  };
}
